import verge from 'verge';
import { offset } from './dom';

// Returns an object that represents a position for a child element according to align element.
// At default, the calculation position child under align element, aligned to it's left side. In addition,
// it takes into consideration the boundaries of the screen or boundaries of an element.
function getChildPosition(
  alignElement,
  child,
  {
    spacePosition = 'bottom',
    alignPositionType = 'relative', // 'relative' or 'absolute'
    horizontalAlign = 'left', // 'left', 'right' or 'center'
    boundariesElement = window,
    hBoundariesElement,
    vBoundariesElement,
    fullWidth = false,
    hasScreenBoundries = false,
    fixedAncestorElement,
    renderOutsideContainer,
    forcedBelow = false
  }
) {
  var boundariesWidth, boundariesHeight, boundariesTop, boundariesLeft;

  if (hasScreenBoundries) {
    boundariesWidth = window.innerWidth;
    boundariesHeight = window.innerHeight;
    boundariesTop = 0;
    boundariesLeft = 0;
  } else {
    if (!hBoundariesElement) {
      hBoundariesElement = boundariesElement;
    }
    if (hBoundariesElement === window) {
      boundariesWidth = window.innerWidth;
      boundariesLeft = 0;
    } else {
      let rect = verge.rectangle(hBoundariesElement);
      boundariesWidth = rect.width;
      boundariesLeft = rect.left;
    }

    if (!vBoundariesElement) {
      vBoundariesElement = boundariesElement;
    }
    if (vBoundariesElement === window) {
      boundariesHeight = window.innerHeight;
      boundariesTop = 0;
    } else {
      let rect = verge.rectangle(vBoundariesElement);
      boundariesHeight = rect.height;
      boundariesTop = rect.top;
    }
  }

  var childWidth = child.offsetWidth;
  var childHeight = child.offsetHeight;

  var alignHeight = alignElement.offsetHeight;
  var alignWidth = alignElement.offsetWidth;

  var alignOffset;
  if (alignPositionType === 'relative') {
    alignOffset = offset(alignElement);
  } else if (alignPositionType === 'absolute') {
    const clientRect = alignElement.getBoundingClientRect();
    alignOffset = {
      top: clientRect.top,
      left: clientRect.left
    };
  }

  if (hasScreenBoundries && !renderOutsideContainer) {
    alignOffset.top -= window.scrollY;
    alignOffset.left -= window.scrollX;
  }

  if (renderOutsideContainer) {
    alignOffset.top += window.scrollY;
    alignOffset.left += window.scrollX;
  }

  var alignLeft = alignOffset.left;
  var alignRight = alignLeft + alignWidth;
  var alignTop = alignOffset.top;

  var spaceTop = alignTop - boundariesTop; // how much space we have above the alignElement and boundariesElement
  var spaceBottom = boundariesHeight - spaceTop - alignHeight; // how much space we have under the alignElement
  var spaceLeft = alignLeft - boundariesLeft; // how much space we have to the left of the alignElement
  var spaceRight = boundariesLeft + boundariesWidth - alignRight; // how much space we have to the right of the alignElement

  var hasSpaceBelow = forcedBelow ? true : childHeight <= spaceBottom;
  var hasSpaceAbove = childHeight <= spaceTop;

  var position = {};
  var vertical = { above: false, below: false, top: false, bottom: false };
  var horizontal = {
    left: false,
    right: false,
    center: false,
    leftBoundary: false,
    rightBoundary: false,
    stretch: false
  };

  function positionBelow() {
    vertical.below = true;
    position.bottom = 'auto';
    if (alignPositionType === 'absolute') {
      position.top = alignTop + alignHeight + 'px';
    } else {
      position.top = alignHeight + 'px';
    }
  }

  function positionAbove() {
    vertical.above = true;
    if (alignPositionType === 'absolute') {
      position.bottom = 'auto';
      position.top = alignTop - childHeight + 'px';
    } else {
      position.bottom = alignHeight + 'px';
      position.top = 'auto';
    }
  }

  function positionAtTop() {
    vertical.top = true;
    if (alignPositionType === 'absolute') {
      position.top = alignTop + 'px';
      position.bottom = 'auto';
    } else {
      position.top = -spaceTop + 'px';
      position.bottom = 'auto';
    }
  }

  function positionAtBottom() {
    vertical.bottom = true;
    if (alignPositionType === 'absolute') {
      position.top = alignTop - childHeight + alignHeight + 'px';
      position.bottom = 'auto';
    } else {
      position.top = 'auto';
      position.bottom = -spaceBottom + 'px';
    }
  }

  switch (spacePosition) {
    case 'bottom':
      if (hasSpaceBelow) {
        positionBelow();
      } else if (hasSpaceAbove) {
        positionAbove();
      } else {
        positionAtBottom();
      }
      break;
    default:
      if (hasSpaceAbove) {
        positionAbove();
      } else if (hasSpaceBelow) {
        positionBelow();
      } else {
        positionAtTop();
      }
  }

  // Vertical align handling
  var childHalfWidth = childWidth / 2;
  var alignHalfWidth = alignWidth / 2;
  var canLeftAlign = alignLeft + childWidth <= boundariesLeft + boundariesWidth;
  var canRightAlign = alignRight - childWidth > 0;
  var canCenterAlign =
    alignRight - childHalfWidth > 0 &&
    alignHalfWidth + spaceRight >= childHalfWidth &&
    alignLeft + childHalfWidth <= boundariesLeft + boundariesWidth;

  var canFitHorizontally = childWidth <= boundariesWidth;
  var takesFullWidth = childWidth === boundariesWidth || fullWidth;

  function alignToLeft() {
    horizontal.left = true;
    if (alignPositionType === 'absolute') {
      position.left = alignLeft + 'px';
      position.right = 'auto';
    } else {
      position.left = '0';
      position.right = 'auto';
    }
  }

  function alignToCenter() {
    horizontal.center = true;
    if (alignPositionType === 'absolute') {
      position.left = alignLeft + alignHalfWidth - childHalfWidth + 'px';
      position.right = 'auto';
    } else {
      let isChildWider = childWidth > alignWidth;
      let left = Math.abs(childWidth - alignWidth) / 2;
      left = isChildWider ? -left : left;
      position.left = left + 'px';
      position.right = 'auto';
    }
  }

  function alignToRight() {
    horizontal.right = true;
    if (alignPositionType === 'absolute') {
      position.left = alignLeft - childWidth + 'px';
      position.right = '0';
    } else {
      position.left = 'auto';
      position.right = '0';
    }
  }

  function alignToLeftBoundary() {
    horizontal.leftBoundary = true;
    if (alignPositionType === 'absolute') {
      position.left = boundariesLeft + 'px';
      position.right = 'auto';
    } else {
      position.left = -spaceLeft + 'px';
      position.right = 'auto';
    }
  }

  function alignToRightBoundary() {
    horizontal.rightBoundary = true;
    if (alignPositionType === 'absolute') {
      position.left = boundariesLeft + boundariesWidth - childWidth + 'px';
      position.right = 'auto';
    } else {
      position.left = 'auto';
      position.right = -spaceRight + 'px';
    }
  }

  function stretchHorizontal() {
    horizontal.stretch = true;
    if (alignPositionType === 'absolute') {
      position.left = boundariesLeft + 'px';
      position.right = boundariesLeft + boundariesWidth + 'px';
    } else {
      position.left = -spaceLeft + 'px';
      position.right = -spaceRight + 'px';
    }
  }

  // if there is no room on both right and left sides
  if (!canFitHorizontally || takesFullWidth) {
    stretchHorizontal();
  } else {
    switch (horizontalAlign) {
      case 'center':
        if (canCenterAlign) {
          alignToCenter();
        } else if (canRightAlign) {
          alignToRight();
        } else if (canLeftAlign) {
          alignToLeft();
        } else {
          alignToLeftBoundary();
        }
        break;
      case 'left':
        if (canLeftAlign) {
          alignToLeft();
        } else if (canCenterAlign) {
          alignToCenter();
        } else if (canRightAlign) {
          alignToRight();
        } else {
          alignToLeftBoundary();
        }
        break;
      case 'right':
        if (canRightAlign) {
          alignToRight();
        } else if (canCenterAlign) {
          alignToCenter();
        } else if (canLeftAlign) {
          alignToLeft();
        } else {
          alignToRightBoundary();
        }
        break;
    }
  }

  if (fixedAncestorElement) {
    let fixedRect = fixedAncestorElement.getBoundingClientRect();

    if (!isNaN(parseInt(position.top))) {
      position.top = parseInt(position.top) - fixedRect.top + 'px';
    }
    if (!isNaN(parseInt(position.left))) {
      position.left = parseInt(position.left) - fixedRect.left + 'px';
    }
    // TODO: complete this computation for right and bottom
  }

  return {
    position,
    canFitHorizontally,
    takesFullWidth,
    vertical,
    horizontal
  };
}

export default getChildPosition;
